import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { DropzoneArea } from "material-ui-dropzone";
import InputMask from "react-input-mask";
import { TopAppBarHeight } from "../../../theme/ComponentTheme";
import { Redirect, useHistory } from "react-router-dom";
import { AccountInfoContext } from "../../../components/AccountInfoContext";
import { LoadingIndicator } from "../../../components/LoadingIndicator";
import { RBAppbar } from "../../../components/RBAppbar";
import { RBAlert } from "../../../components/RBAlert";
import { RBButton } from "../../../components/RBButton";
import { RBContext } from "../../../components/RBContext";
import { renderRBPayText } from "../../../components/RBPayWordmark";
import { RBAdminEmail, RoutePathConstant } from "../../../util/Common";
import { AuthService } from "../../../api/AuthService";
import { isRBPayAvailableForWorker } from "../../../util/rbPay";
import {
  RB_PAY_ALLOWED_DOCUMENT_TYPES,
  RB_PAY_BANK_DOCUMENT_HELPER_TEXT,
  RB_PAY_BUSINESS_CERTIFICATION_TEXT,
  RB_PAY_BUSINESS_DESCRIPTION,
  RB_PAY_BUSINESS_TYPE_OPTIONS,
  RB_PAY_DIRECT_DEPOSIT_AUTHORIZATION_TEXT,
  RB_PAY_EMAIL_CERTIFICATION_TEXT,
  RB_PAY_EMPLOYER_NOTE,
  RB_PAY_MAX_DOCUMENT_SIZE,
  RB_PAY_PHARMA_SIN_HELPER_TEXT,
  RB_PAY_SETUP_COLORS,
  RB_PAY_SETUP_NOTES,
  RB_PAY_STATEMENT_ACCESS_NOTE,
  RB_PAY_TAX_DESCRIPTION,
  RB_PAY_TAX_PROFILE_OPTIONS,
  RB_PAY_FIRST_SUBMIT_TITLE,
  RB_PAY_FIRST_SUBMIT_MESSAGE,
  RB_PAY_EDIT_SUBMIT_TITLE,
  RB_PAY_EDIT_SUBMIT_MESSAGE,
  RB_PAY_SUBMIT_ERROR_TITLE,
  RB_PAY_SUBMIT_ERROR_MESSAGE,
  RB_PAY_NO_CHANGES_TOOLTIP,
} from "../constants/rbPaySettings.constants";
import { RBPayDocumentPreviewModal } from "../components/RBPayDocumentPreviewModal";
import { RBPayEmailVerificationModal } from "../components/RBPayEmailVerificationModal";
import { RBPayChoiceCard, RBPayChoiceGroup } from "../components/RBPayChoiceCard";
import { RBPayStepProgress, RBPayStepNotice } from "../components/RBPayStepProgress";
import { RBPaySetupSection } from "../components/RBPaySetupSection";
import { useRBPaySetup } from "../hooks/useRBPaySetup";
import {
  RBPayBusinessType,
  RBPayEmploymentType,
  RBPayPaymentMethod,
  RBPayTaxProfile,
} from "../types/rbPaySettings.types";
import {
  formatBusinessNumber,
  getBusinessNumberPrefixForType,
  isImageDocument,
  maskAccountNumber,
  maskSin,
} from "../utils/rbPaySettings.formatters";
import {
  getMaxDateOfBirthString,
  MINIMUM_DATE_OF_BIRTH,
} from "../validators/rbPaySettings.validators";
import { buildRBPaySetupTheme, RBPaySetupTheme } from "../utils/rbPaySettings.theme";

const C = RB_PAY_SETUP_COLORS;

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: C.pageBg,
  },
  header: {
    // Normal flow (not sticky): this screen renders inside the app shell, which
    // already provides the top chrome - a second sticky bar overlapped content.
    position: "relative",
    background: "#fff",
    borderBottom: `1px solid ${C.cardBorder}`,
  },
  headerInner: {
    maxWidth: 840,
    margin: "0 auto",
    padding: "0 32px",
    minHeight: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "12px 20px",
    },
  },
  wordmark: {
    fontSize: 18,
    fontWeight: 700,
    color: C.textDark,
    letterSpacing: "-0.02em",
  },
  wordmarkEm: {
    fontStyle: "italic",
    color: (p: RBPaySetupTheme) => p.primary,
  },
  headerNote: {
    fontSize: 11,
    color: C.textMuted,
    margin: "1px 0 0",
  },
  statementsButton: {
    padding: "8px 20px",
    background: (p: RBPaySetupTheme) => p.primary,
    color: "#fff",
    border: "none",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  content: {
    maxWidth: 840,
    margin: "0 auto",
    padding: "28px 32px 64px",
    [theme.breakpoints.down("xs")]: {
      padding: "20px 16px 48px",
    },
  },
  topActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 16,
  },
  appbarActions: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  appbarStatements: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  infoIcon: {
    fontSize: 18,
    color: C.textMuted,
    cursor: "help",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: "3px 10px",
    whiteSpace: "nowrap",
  },
  statusActive: {
    color: C.successText,
    background: C.successBg,
    border: `1px solid ${C.successBorder}`,
  },
  statusPending: {
    color: C.warnText,
    background: C.warnBg,
    border: `1px solid ${C.warnBorder}`,
  },
  statusNotStarted: {
    color: C.textLabel,
    background: C.summaryBg,
    border: `1px solid ${C.summaryBorder}`,
  },
  statusOnHold: {
    color: C.errorText,
    background: C.errorBg,
    border: `1px solid ${C.errorBorder}`,
  },
  stickyChecklist: {
    position: "sticky",
    top: TopAppBarHeight,
    zIndex: 50,
    background: C.pageBg,
  },
  banner: {
    background: (p: RBPaySetupTheme) => `linear-gradient(135deg, ${p.bannerFrom} 0%, ${p.bannerTo} 100%)`,
    borderRadius: 12,
    padding: "22px 28px",
    marginBottom: 18,
  },
  bannerHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: 10,
  },
  bannerIcon: {
    width: 30,
    height: 30,
    borderRadius: 7,
    background: "rgba(255,255,255,0.13)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  bannerTitle: {
    fontSize: 14.5,
    fontWeight: 700,
    color: "#fff",
    margin: 0,
  },
  bannerBody: {
    fontSize: 13,
    lineHeight: 1.65,
    color: "rgba(255,255,255,0.87)",
    margin: "0 0 14px",
  },
  bannerFooter: {
    borderTop: "1px solid rgba(255,255,255,0.13)",
    paddingTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  bannerMeta: {
    fontSize: 12,
    color: "rgba(255,255,255,0.72)",
    margin: 0,
    lineHeight: 1.5,
  },
  bannerLink: {
    color: "#fff",
    fontWeight: 500,
  },
  groupLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.textLabel,
    margin: "0 0 10px",
  },
  blockGap: {
    marginBottom: 22,
  },
  inputLabel: {
    display: "block",
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.textLabel,
    marginBottom: 7,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: `1.5px solid ${C.optionOffBorder}`,
    borderRadius: 8,
    fontSize: 14,
    color: C.textDark,
    background: C.optionOffBg,
  },
  fieldError: {
    color: C.errorText,
    fontSize: 12.5,
    minHeight: 20,
    marginTop: 5,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
    marginBottom: 16,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  subHeading: {
    fontSize: 13.5,
    fontWeight: 600,
    color: C.textDark,
    margin: "0 0 4px",
  },
  helperRow: {
    fontSize: 12.5,
    color: C.textMuted,
    lineHeight: 1.5,
    margin: 0,
  },
  noteBox: {
    padding: "12px 16px",
    background: C.noteBg,
    border: `1px solid ${C.noteBorder}`,
    borderRadius: 8,
  },
  noteText: {
    fontSize: 12.5,
    lineHeight: 1.55,
    margin: 0,
  },
  checkboxBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "14px 16px",
    background: C.noteBg,
    border: `1px solid ${C.noteBorder}`,
    borderRadius: 8,
  },
  checkbox: {
    width: 15,
    height: 15,
    flexShrink: 0,
    marginTop: 2,
    cursor: "pointer",
    accentColor: (p: RBPaySetupTheme) => p.secondary,
  },
  checkboxLabel: {
    fontSize: 13,
    color: C.textBody,
    lineHeight: 1.6,
    cursor: "pointer",
  },
  sectionDivider: {
    borderTop: `1px solid ${C.innerDivider}`,
    paddingTop: 20,
  },
  uploadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 9,
    gap: "8px",
  },
  uploadButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    background: (p: RBPaySetupTheme) => p.primary,
    color: "#fff",
    border: "none",
    borderRadius: 7,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  },
  dropzoneUploaded: {
    border: `1.5px dashed #CCC5E0`,
    borderRadius: 10,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    background: C.optionOffBg,
  },
  // material-ui-dropzone renders a tall centred box by default; these overrides
  // collapse it to a compact single row (small icon on the left, text on the
  // right) to match the mobile RB Pay upload field. Behaviour is unchanged.
  dropzoneEmpty: {
    minHeight: "0 !important",
    padding: "12px 14px !important",
    borderRadius: "10px !important",
    "& .MuiDropzoneArea-textContainer": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "12px",
    },
    "& .MuiDropzoneArea-text": {
      order: 2,
      fontSize: 14,
      fontWeight: 600,
      color: C.textDark,
      margin: 0,
    },
    "& .MuiDropzoneArea-icon": {
      order: 1,
      width: 34,
      height: 34,
      color: "#9B8FC0",
      flexShrink: 0,
    },
  },
  dropzonePrimary: {
    fontSize: 14,
    fontWeight: 600,
    color: C.textDark,
    margin: 0,
  },
  uploadHelp: {
    fontSize: 11.5,
    color: C.textFaint,
    margin: "7px 0 0",
  },
  filePreviewImg: {
    width: 44,
    height: 44,
    borderRadius: 8,
    objectFit: "cover",
    flexShrink: 0,
  },
  fileActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: 10,
  },
  linkButton: {
    background: "transparent",
    border: "none",
    color: (p: RBPaySetupTheme) => p.secondary,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
  },
  sendCodeButton: {
    padding: "9px 18px",
    background: (p: RBPaySetupTheme) => p.secondarySoft,
    color: (p: RBPaySetupTheme) => p.secondary,
    border: (p: RBPaySetupTheme) => `1.5px solid ${p.secondaryBorder}`,
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    "&:disabled": {
      opacity: 0.55,
      cursor: "not-allowed",
    },
  },
  verifiedText: {
    fontSize: 12.5,
    color: C.successIcon,
    margin: "0 0 12px",
  },
  statusHeadingRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: 16,
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: "3px 10px",
    whiteSpace: "nowrap",
  },
  chipVerified: {
    color: C.successText,
    background: C.successBg,
    border: `1px solid ${C.successBorder}`,
  },
  chipUnverified: {
    color: C.warnText,
    background: C.warnBg,
    border: `1px solid ${C.warnBorder}`,
  },
  emailNotice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "10px 14px",
    background: C.warnBg,
    border: `1px solid ${C.warnBorder}`,
    borderRadius: 8,
    fontSize: 12.5,
    color: C.warnText,
    lineHeight: 1.5,
  },
  reverifyHint: {
    fontSize: 12,
    color: C.textMuted,
    margin: "8px 0 0",
  },
  summaryBox: {
    background: C.summaryBg,
    border: `1px solid ${C.summaryBorder}`,
    borderRadius: 9,
    overflow: "hidden",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "11px 16px",
    gap: "16px",
  },
  summaryKey: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: C.textLabel,
  },
  summaryValue: {
    fontSize: 13.5,
    fontWeight: 500,
    color: C.textDark,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
  successBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "14px 16px",
    background: C.successBg,
    border: `1px solid ${C.successBorder}`,
    borderRadius: 8,
  },
  successText: {
    fontSize: 13,
    color: C.successText,
    lineHeight: 1.55,
    margin: 0,
  },
  pharmaNote: {
    fontSize: 12.5,
    color: C.textMuted,
    lineHeight: 1.5,
    margin: "12px 0 0",
    whiteSpace: "pre-wrap",
  },
  sectionError: {
    color: C.errorText,
    fontSize: 13,
    marginTop: 14,
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: 4,
  },
  cancelButton: {
    padding: "11px 24px",
    background: "transparent",
    color: "#6B5F8A",
    border: `1.5px solid #CCC5E0`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  saveButton: {
    padding: "11px 32px",
    background: (p: RBPaySetupTheme) => p.primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: "0.01em",
    "&:disabled": {
      opacity: 0.55,
      cursor: "not-allowed",
    },
  },
}));

const useSummaryRowStyles = makeStyles({
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "11px 16px",
    gap: "16px",
  },
  key: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: C.textLabel,
  },
  value: {
    fontSize: 13.5,
    fontWeight: 500,
    color: C.textDark,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
});

const SummaryRow = ({
  label,
  value,
  divider,
}: {
  label: string;
  value: React.ReactNode;
  divider: boolean;
}) => {
  const classes = useSummaryRowStyles();
  return (
    <div
      className={classes.row}
      style={divider ? { borderBottom: `1px solid ${C.summaryBorder}` } : undefined}
    >
      <span className={classes.key}>{label}</span>
      <span className={classes.value}>{value}</span>
    </div>
  );
};

const UploadDropIcon: React.FC = () => (
  <svg width="38" height="38" viewBox="0 0 36 36" fill="none">
    <rect width="36" height="36" rx="8" fill="#EDE8F7" />
    <path d="M18 11v11M18 11l-4 4.5M18 11l4 4.5" stroke="#9B8FC0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 25h16" stroke="#C8C0DC" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const RBPaySettingsContentPage: React.FC = () => {
  const history = useHistory();
  const { industryMode, primaryColor, secondaryColor } = useContext(RBContext);
  const brandTheme = useMemo(
    () => buildRBPaySetupTheme(primaryColor, secondaryColor),
    [primaryColor, secondaryColor]
  );
  const classes = useStyles(brandTheme);
  const { accountInformation, fetchAccountInfo, isPageLoading } = useContext(AccountInfoContext);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [successModal, setSuccessModal] = useState<null | "first" | "edit">(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const checklistRef = useRef<HTMLDivElement | null>(null);
  const profileSectionRef = useRef<HTMLDivElement | null>(null);
  const paymentSectionRef = useRef<HTMLDivElement | null>(null);
  const authSectionRef = useRef<HTMLDivElement | null>(null);

  const isEligible = isRBPayAvailableForWorker({
    industryMode,
    roleName: AuthService.getRoleName(),
    account: accountInformation,
  });

  const setup = useRBPaySetup({ account: accountInformation, industryMode });

  // Update the account information when the component mounts.
  useEffect(() => {
    fetchAccountInfo();
  }, []);

  const {
    mode,
    draft,
    industryConfig,
    supportsProfileTypeSelection,
    contactEmail,
    statementsUrl,
    steps,
    activeStepKey,
    isSetupComplete,
    isEditing,
    isDirty,
    isSaving,
    isSaveDisabled,
    errorMessage,
    documentUploadError,
    setDocumentUploadError,
  } = setup;

  const isDirectDeposit = draft.paymentMethod === RBPayPaymentMethod.DIRECT_DEPOSIT;
  const isEmployee = draft.employmentType === RBPayEmploymentType.EMPLOYEE;
  const isBusiness = draft.employmentType === RBPayEmploymentType.BUSINESS;

  const contactEmailAddress = contactEmail || RBAdminEmail;
  // Requirement: mailto opens the user's client with a "Questions about RB Pay"
  // subject. (CC of the network admin is a backend-provided address.)
  const contactMailto = `mailto:${contactEmailAddress}?subject=${encodeURIComponent("Questions about RB Pay")}`;

  // Render the validation slot only when there is a message, so empty fields
  // don't reserve phantom vertical space (keeps rhythm consistent with the
  // radio groups, which already render their errors conditionally).
  const renderFieldError = (message?: string) =>
    message ? <div className={classes.fieldError}>{message}</div> : null;

  // Single application: the whole form is editable together (setup, or a saved
  // record the user clicked Edit on), otherwise everything is read-only.
  const isFormMode = mode === "setup" || isEditing;
  // Recomputed per render (cheap) so a session left open past midnight still
  // caps the Date of Birth picker at the correct "yesterday".
  const maxDateOfBirth = getMaxDateOfBirthString();

  const openStatements = () => {
    if (statementsUrl) {
      window.open(statementsUrl, "_blank", "noopener,noreferrer");
    }
  };

  // material-ui-dropzone hands us File[] via a callback (no synthetic event),
  // so there is no React event-pooling hazard and no input.value to reset.
  const handleDropFiles = async (files: File[]) => {
    const file = files && files[0];
    if (!file) {
      return;
    }

    if (!RB_PAY_ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      setDocumentUploadError("Please upload a PDF, PNG, JPG, or JPEG file.");
      setup.markFieldTouched("directDeposit.document");
      return;
    }

    if (file.size > RB_PAY_MAX_DOCUMENT_SIZE) {
      setDocumentUploadError("Document must be 10 MB or less.");
      setup.markFieldTouched("directDeposit.document");
      return;
    }

    setDocumentUploadError("");
    setIsUploading(true);

    try {
      await setup.uploadDocument(file);
    } catch (error) {
      const err: any = error;
      setDocumentUploadError(err?.message || "Unable to upload document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDropRejected = () => {
    setDocumentUploadError("Please upload a PDF, PNG, or JPG file under 10 MB.");
    setup.markFieldTouched("directDeposit.document");
  };

  const handleCancel = () => {
    if (mode === "setup") {
      history.push(RoutePathConstant.Profile);
      return;
    }
    setup.cancelEdit();
  };

  // Pharma SIN/Business is configured on the SIN/Business registration FORM
  // (SINBusinessInfoPage). Navigate straight to the form — not the summary page
  // (SINBusinessSetting) — so the form's history.goBack() on submit returns here
  // instead of to the summary. The form reads location.state.updateBusinessInfo
  // unguarded (throws without state), so always pass it; `false` shows the
  // SIN-vs-business chooser. On return, the mount refresh repopulates the value.
  const goToSinBusinessSetup = () => {
    // Stash in-progress edits before the route change unmounts this page, so the
    // worker's payment details are restored on return (see useRBPaySetup).
    setup.preserveDraftForReturn();
    history.push(RoutePathConstant.SINBusinessRegistrationSetting, { updateBusinessInfo: false });
  };

  const sectionRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    profile: profileSectionRef,
    payment: paymentSectionRef,
    authorization: authSectionRef,
  };

  // Scroll a section just below the sticky app bar + sticky checklist.
  const scrollToSection = (key: string) => {
    const element = sectionRefs[key]?.current;
    if (!element) {
      return;
    }

    const checklistHeight = checklistRef.current?.offsetHeight || 0;
    const targetY =
      element.getBoundingClientRect().top + window.scrollY - TopAppBarHeight - checklistHeight - 16;
    window.scrollTo({ top: Math.max(targetY, 0), behavior: "smooth" });
  };

  // Guided submit: validate, reveal errors, and jump to the first incomplete
  // section. `save()` only persists (returns true) when everything is valid;
  // on success we show the submission-success modal.
  const handleSave = async () => {
    const wasFirstTime = mode === "setup";
    const firstInvalid = setup.getFirstInvalidSection();
    const result = await setup.save();

    if (result === "saved") {
      setSuccessModal(wasFirstTime ? "first" : "edit");
    } else if (result === "invalid") {
      if (firstInvalid) {
        window.setTimeout(() => scrollToSection(firstInvalid), 60);
      }
    } else if (result === "error") {
      setErrorModalOpen(true);
    }
    // "pending-verification" → the verification modal is already open; no-op.
  };

  const closeSuccessModalAndRedirect = () => {
    setSuccessModal(null);
    // US6559: when RB Pay setup was opened from the shift apply gate, return to that
    // shift so the worker can apply; otherwise fall back to the dashboard.
    const returnTo = (history.location.state as { from?: string } | undefined)?.from;
    history.push(returnTo || RoutePathConstant.DashBoard);
  };

  const rbPayStatus = setup.serverState.approvalInfo.status;
  const rbPayStatusLabel =
    rbPayStatus === "active"
      ? "Active"
      : rbPayStatus === "on-hold"
        ? "On Hold"
        : rbPayStatus === "pending"
          ? "Pending"
          : "Not Started";
  const rbPayStatusClass =
    rbPayStatus === "active"
      ? classes.statusActive
      : rbPayStatus === "on-hold"
        ? classes.statusOnHold
        : rbPayStatus === "pending"
          ? classes.statusPending
          : classes.statusNotStarted;

  // State-aware checklist notice: editing/first-time setup shows an in-progress
  // "Save Setup" prompt; a saved-but-unapproved setup reads as "submitted"; an
  // approved/active setup reads as "complete".
  const setupNotice = useMemo<RBPayStepNotice>(() => {
    if (isFormMode) {
      return {
        tone: "warn",
        message: isEditing ? RB_PAY_SETUP_NOTES.editing : RB_PAY_SETUP_NOTES.incomplete,
      };
    }
    if (rbPayStatus === "on-hold") {
      return { tone: "warn", message: RB_PAY_SETUP_NOTES.onHold };
    }
    if (rbPayStatus === "pending") {
      return { tone: "warn", message: RB_PAY_SETUP_NOTES.submitted };
    }
    return { tone: "success", message: RB_PAY_SETUP_NOTES.complete };
  }, [isFormMode, isEditing, rbPayStatus]);

  const appbarActions = (
    <div className={classes.appbarActions}>
      <span className={`${classes.statusPill} ${rbPayStatusClass}`}>{rbPayStatusLabel}</span>
      <div className={classes.appbarStatements}>
        <button
          type="button"
          className={classes.statementsButton}
          onClick={openStatements}
          disabled={!statementsUrl}
        >
          View Statements
        </button>
        <Tooltip title={RB_PAY_STATEMENT_ACCESS_NOTE} arrow placement="bottom-end">
          <InfoOutlined className={classes.infoIcon} aria-label={RB_PAY_STATEMENT_ACCESS_NOTE} />
        </Tooltip>
      </div>
    </div>
  );

  if (isPageLoading || setup.isPageLoading) {
    return (
      <>
        <RBAppbar barName="RB Pay Settings" />
        <LoadingIndicator />
      </>
    );
  }

  if (!isEligible) {
    return <Redirect to={RoutePathConstant.Profile} />;
  }

  const saveLabel = "Save Setup";

  // ── Profile section ───────────────────────────────────────────────
  const renderProfileBody = () => {
    if (!supportsProfileTypeSelection) {
      // Pharma: SIN/Business is managed on the SIN/Business Settings page (stored
      // on the account), not edited here. Show whichever is on file read-only, or
      // a "not set up" prompt that deep-links to the SIN/Business Settings page.
      const pharmacistInfo = accountInformation?.pharmacist || {};
      const businessNumberOnFile = String(pharmacistInfo.taxNumber || "").trim();
      const businessNameOnFile = String(pharmacistInfo.businessName || "").trim();
      const hasBusiness = Boolean(businessNumberOnFile);

      if (setup.pharmaProfileMissing) {
        return (
          <>
            <div
              style={{
                border: `1px solid ${C.summaryBorder}`,
                background: C.summaryBg,
                borderRadius: 9,
                padding: 16,
                marginBottom: 14,
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, color: C.textDark, fontSize: 14 }}>
                {renderRBPayText("SIN / Business information not set up")}
              </p>
              <p style={{ margin: "6px 0 0", color: C.textMuted, fontSize: 13, lineHeight: 1.5 }}>
                {renderRBPayText(
                  "Set up your SIN or Business Number to continue with RB Pay. You'll be brought back here once it's saved."
                )}
              </p>
            </div>
            <button type="button" className={classes.saveButton} onClick={goToSinBusinessSetup}>
              Set up SIN / Business Information
            </button>
          </>
        );
      }

      return (
        <>
          <div className={classes.summaryBox}>
            {hasBusiness ? (
              <>
                <SummaryRow label="Business Name" value={businessNameOnFile || "—"} divider />
                <SummaryRow
                  label="Business #"
                  value={formatBusinessNumber(businessNumberOnFile) || "—"}
                  divider={false}
                />
              </>
            ) : (
              <>
                <SummaryRow
                  label="SIN"
                  value={maskSin(draft.employeeProfile.sinNumber) || "—"}
                  divider={!isFormMode}
                />
                {!isFormMode ? (
                  <SummaryRow
                    label="Date of Birth"
                    value={draft.employeeProfile.dateOfBirth || "—"}
                    divider={false}
                  />
                ) : null}
              </>
            )}
          </div>

          {!hasBusiness ? (
            <p className={classes.pharmaNote}>
              {RB_PAY_PHARMA_SIN_HELPER_TEXT.replace(
                "{contactEmail}",
                // Use the live per-network contact email (same source as the
                // SIN/Business Settings page) so it reflects the current network.
                localStorage.getItem("contactEmail") || contactEmail || RBAdminEmail
              )}
            </p>
          ) : null}

          {/* Pharma workers with a SIN are `employee` profiles, and the backend
              requires a DOB for those regardless of industry — so this is the one
              RB Pay profile field a pharma worker fills in here. Business-registered
              pharma workers are `business` profiles and don't need it.
              Rendered BELOW the "contact us to change the above" note, which refers
              to the read-only SIN box — this field is editable, so it must not sit
              above that sentence. */}
          {!hasBusiness && isFormMode ? (
            <div style={{ marginTop: 14, maxWidth: 280 }}>
              <label className={classes.inputLabel}>Date of Birth</label>
              <input
                type="date"
                className={classes.input}
                value={draft.employeeProfile.dateOfBirth}
                min={MINIMUM_DATE_OF_BIRTH}
                max={maxDateOfBirth}
                placeholder="yyyy-mm-dd"
                onChange={(event) => setup.updateDraft("employeeProfile.dateOfBirth", event.target.value)}
                onBlur={() => setup.markFieldTouched("employeeProfile.dateOfBirth")}
              />
              {renderFieldError(setup.getFieldError("employeeProfile.dateOfBirth"))}
            </div>
          ) : null}
        </>
      );
    }

    if (!isFormMode) {
      return (
        <div className={classes.summaryBox}>
          <SummaryRow label="Profile Type" value={isEmployee ? "Employee" : "Business"} divider />
          {isEmployee ? (
            <>
              <SummaryRow label="SIN" value={maskSin(draft.employeeProfile.sinNumber) || "—"} divider />
              <SummaryRow label="Date of Birth" value={draft.employeeProfile.dateOfBirth || "—"} divider />
              <SummaryRow
                label="Tax Profile"
                value={draft.employeeProfile.taxProfile === RBPayTaxProfile.LOW ? "Low Tax Profile" : "High Tax Profile"}
                divider={false}
              />
            </>
          ) : (
            <>
              <SummaryRow
                label="Business Type"
                value={draft.businessProfile.businessType === RBPayBusinessType.INCORPORATED ? "Incorporated" : "Sole Proprietor"}
                divider
              />
              <SummaryRow label="Business Name" value={draft.businessProfile.legalBusinessName || "—"} divider />
              <SummaryRow label="Business #" value={draft.businessProfile.businessNumber || "—"} divider={false} />
            </>
          )}
        </div>
      );
    }

    return (
      <>
        <div className={classes.blockGap}>
          <p className={classes.groupLabel}>Preferred Payment Profile Type</p>
          <RBPayChoiceGroup errorMessage={setup.getFieldError("employmentType")}>
            <RBPayChoiceCard
              title="Employee"
              checked={isEmployee}
              theme={brandTheme}
              onSelect={() => setup.updateDraft("employmentType", RBPayEmploymentType.EMPLOYEE)}
            />
            <RBPayChoiceCard
              title="Business"
              checked={isBusiness}
              theme={brandTheme}
              onSelect={() => setup.updateDraft("employmentType", RBPayEmploymentType.BUSINESS)}
            />
          </RBPayChoiceGroup>
        </div>

        {isEmployee ? (
          <>
            {/* SIN and Date of Birth share a row, per the mockup. DOB is collected
                for Employee profiles only; the same 1fr/1fr grid as the business
                fields collapses to a single column on narrow screens. */}
            <div className={classes.twoCol}>
              <div>
                <label className={classes.inputLabel}>SIN #</label>
                <input
                  className={classes.input}
                  value={draft.employeeProfile.sinNumber}
                  placeholder="000-000-000"
                  onChange={(event) => setup.updateDraft("employeeProfile.sinNumber", event.target.value)}
                  onBlur={() => setup.markFieldTouched("employeeProfile.sinNumber")}
                />
                {renderFieldError(setup.getFieldError("employeeProfile.sinNumber"))}
              </div>
              <div>
                <label className={classes.inputLabel}>Date of Birth</label>
                {/* Native date control: opens the browser's calendar picker and
                    still accepts typing, keeping the RB Pay card's own input
                    styling (an MUI picker would not match). Its value is always a
                    "YYYY-MM-DD" string — the exact wire format — so no parsing or
                    timezone conversion happens anywhere. min/max make today, any
                    future date and pre-1900 unpickable; the validator still checks
                    them for typed input. */}
                <input
                  type="date"
                  className={classes.input}
                  value={draft.employeeProfile.dateOfBirth}
                  min={MINIMUM_DATE_OF_BIRTH}
                  max={maxDateOfBirth}
                  placeholder="yyyy-mm-dd"
                  onChange={(event) => setup.updateDraft("employeeProfile.dateOfBirth", event.target.value)}
                  onBlur={() => setup.markFieldTouched("employeeProfile.dateOfBirth")}
                />
                {renderFieldError(setup.getFieldError("employeeProfile.dateOfBirth"))}
              </div>
            </div>

            <div>
              <p className={classes.subHeading}>Tax Profile (Employee)</p>
              <p className={classes.helperRow} style={{ marginBottom: 12 }}>
                Choose how much tax is deducted from your pay:
              </p>
              <div style={{ marginBottom: 16 }}>
                <RBPayChoiceGroup errorMessage={setup.getFieldError("employeeProfile.taxProfile")}>
                  {RB_PAY_TAX_PROFILE_OPTIONS.map((option) => (
                    <RBPayChoiceCard
                      key={option.id}
                      title={option.title}
                      description={option.description}
                      checked={draft.employeeProfile.taxProfile === option.id}
                      theme={brandTheme}
                      onSelect={() => setup.updateDraft("employeeProfile.taxProfile", option.id)}
                    />
                  ))}
                </RBPayChoiceGroup>
              </div>
              <div className={classes.noteBox}>
                <p className={classes.noteText} style={{ color: "#6B5F8A", marginBottom: 3 }}>
                  {RB_PAY_TAX_DESCRIPTION}
                </p>
                <p className={classes.noteText} style={{ color: C.textMuted }}>
                  {RB_PAY_EMPLOYER_NOTE}
                </p>
              </div>
            </div>
          </>
        ) : null}

        {isBusiness ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <p className={classes.groupLabel}>Business Type</p>
              <div style={{ marginBottom: 10 }}>
                <RBPayChoiceGroup errorMessage={setup.getFieldError("businessProfile.businessType")}>
                  {RB_PAY_BUSINESS_TYPE_OPTIONS.map((option) => (
                    <RBPayChoiceCard
                      key={option.id}
                      title={option.title}
                      checked={draft.businessProfile.businessType === option.id}
                      theme={brandTheme}
                      onSelect={() => setup.updateDraft("businessProfile.businessType", option.id)}
                    />
                  ))}
                </RBPayChoiceGroup>
              </div>
              <p className={classes.helperRow}>{RB_PAY_BUSINESS_DESCRIPTION}</p>
            </div>

            <div className={classes.twoCol}>
              <div>
                <label className={classes.inputLabel}>Legal Business Name</label>
                <input
                  className={classes.input}
                  value={draft.businessProfile.legalBusinessName}
                  placeholder="Your business name"
                  onChange={(event) => setup.updateDraft("businessProfile.legalBusinessName", event.target.value)}
                  onBlur={() => setup.markFieldTouched("businessProfile.legalBusinessName")}
                />
                {renderFieldError(setup.getFieldError("businessProfile.legalBusinessName"))}
              </div>
              <div>
                <label className={classes.inputLabel}>Business #</label>
                {/* Same input mask as the RB-Portal Business # field: the RT/RC
                    program-account code is fixed by business type, only the 9-digit
                    root and 4-digit suffix are editable. maskChar={null} keeps the
                    000-000-000 default a real value (no placeholder reconciliation). */}
                <InputMask
                  mask={`999-999-999 ${getBusinessNumberPrefixForType(
                    draft.businessProfile.businessType
                  )}9999`}
                  maskChar={null}
                  className={classes.input}
                  value={draft.businessProfile.businessNumber}
                  placeholder={
                    draft.businessProfile.businessType === RBPayBusinessType.INCORPORATED
                      ? "123-456-789 RC0001"
                      : "123-456-789 RT0001"
                  }
                  onChange={(event) => setup.updateDraft("businessProfile.businessNumber", event.target.value)}
                  onBlur={() => setup.markFieldTouched("businessProfile.businessNumber")}
                />
                {renderFieldError(setup.getFieldError("businessProfile.businessNumber"))}
              </div>
            </div>

            <div className={classes.checkboxBox}>
              <input
                type="checkbox"
                id="biz-cert"
                className={classes.checkbox}
                checked={draft.businessProfile.certificationAccepted}
                onChange={(event) => setup.updateDraft("businessProfile.certificationAccepted", event.target.checked)}
              />
              <label htmlFor="biz-cert" className={classes.checkboxLabel}>
                {RB_PAY_BUSINESS_CERTIFICATION_TEXT}
              </label>
            </div>
            {renderFieldError(setup.getFieldError("businessProfile.certificationAccepted"))}
          </>
        ) : null}

        <p className={classes.helperRow} style={{ marginTop: 18 }}>
          Questions? Contact us{" "}
          <a href={contactMailto} style={{ color: brandTheme.secondary, fontWeight: 600 }}>
            {contactEmailAddress}
          </a>
        </p>
      </>
    );
  };

  // ── Payment section ───────────────────────────────────────────────
  const renderPaymentBody = () => {
    if (!isFormMode) {
      return (
        <div className={classes.summaryBox}>
          <SummaryRow
            label="Method"
            value={isDirectDeposit ? "Direct Deposit" : "Email Money Transfer"}
            divider
          />
          {isDirectDeposit ? (
            <>
              <SummaryRow label="Institution #" value={draft.directDeposit.institutionNumber || "—"} divider />
              <SummaryRow label="Transit #" value={draft.directDeposit.transitNumber || "—"} divider />
              <SummaryRow
                label="Account #"
                value={maskAccountNumber(draft.directDeposit.accountNumber) || "—"}
                divider
              />
              <SummaryRow
                label="Document"
                value={
                  draft.directDeposit.document ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: C.successIcon }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6.5" fill="#DCFCE7" />
                        <path d="M4 7l2.5 2.5 4-4" stroke={C.successIcon} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Uploaded
                    </span>
                  ) : (
                    "—"
                  )
                }
                divider={false}
              />
            </>
          ) : (
            <SummaryRow
              label="Email"
              value={
                draft.emailTransfer.emailVerified ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    {draft.emailTransfer.email}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" fill="#DCFCE7" />
                      <path d="M4 7l2.5 2.5 4-4" stroke={C.successIcon} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : (
                  draft.emailTransfer.verifiedEmail || draft.emailTransfer.email || "—"
                )
              }
              divider={false}
            />
          )}
        </div>
      );
    }

    const isEmailVerified = draft.emailTransfer.emailVerified;
    const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((draft.emailTransfer.email || "").trim());

    return (
      <>
        <div style={{ marginBottom: 20 }}>
          <p className={classes.groupLabel}>Preferred Payment Method</p>
          <div style={{ marginBottom: 10 }}>
            <RBPayChoiceGroup errorMessage={setup.getFieldError("paymentMethod")}>
              <RBPayChoiceCard
                title="Direct Deposit"
                description="To receive payments"
                checked={isDirectDeposit}
                theme={brandTheme}
                onSelect={() => {
                  setDocumentUploadError("");
                  setup.updateDraft("paymentMethod", RBPayPaymentMethod.DIRECT_DEPOSIT);
                }}
              />
              <RBPayChoiceCard
                title="Email Money Transfer"
                description="To receive payments"
                checked={!isDirectDeposit}
                theme={brandTheme}
                onSelect={() => {
                  setDocumentUploadError("");
                  setup.updateDraft("paymentMethod", RBPayPaymentMethod.EMAIL_TRANSFER);
                }}
              />
            </RBPayChoiceGroup>
          </div>
          <p className={classes.helperRow}>
            {renderRBPayText(
              "Please select the method you wish to be paid. This will be your default setting for all payments received through RB Pay."
            )}
          </p>
        </div>

        {isDirectDeposit ? (
          <div className={classes.sectionDivider}>
            <p className={classes.subHeading} style={{ marginBottom: 18 }}>
              Please add your bank account information
            </p>
            <div className={classes.twoCol}>
              <div>
                <label className={classes.inputLabel}>Institution Number</label>
                <input
                  className={classes.input}
                  value={draft.directDeposit.institutionNumber}
                  inputMode="numeric"
                  maxLength={3}
                  onChange={(event) => setup.updateDraft("directDeposit.institutionNumber", event.target.value)}
                  onBlur={() => setup.markFieldTouched("directDeposit.institutionNumber")}
                />
                {renderFieldError(setup.getFieldError("directDeposit.institutionNumber"))}
              </div>
              <div>
                <label className={classes.inputLabel}>Transit Number</label>
                <input
                  className={classes.input}
                  value={draft.directDeposit.transitNumber}
                  inputMode="numeric"
                  maxLength={5}
                  onChange={(event) => setup.updateDraft("directDeposit.transitNumber", event.target.value)}
                  onBlur={() => setup.markFieldTouched("directDeposit.transitNumber")}
                />
                {renderFieldError(setup.getFieldError("directDeposit.transitNumber"))}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label className={classes.inputLabel}>Account Number</label>
              <input
                className={classes.input}
                style={{ maxWidth: 340 }}
                value={draft.directDeposit.accountNumber}
                inputMode="numeric"
                maxLength={12}
                onChange={(event) => setup.updateDraft("directDeposit.accountNumber", event.target.value)}
                onBlur={() => setup.markFieldTouched("directDeposit.accountNumber")}
              />
              {renderFieldError(setup.getFieldError("directDeposit.accountNumber"))}
            </div>

            <div className={classes.uploadRow}>
              <label className={classes.inputLabel} style={{ marginBottom: 0 }}>
                Upload Document
              </label>
            </div>

            {draft.directDeposit.document ? (
              <>
                <div className={classes.dropzoneUploaded}>
                  {draft.directDeposit.document.previewUrl &&
                  isImageDocument(draft.directDeposit.document.type) ? (
                    <img
                      src={draft.directDeposit.document.previewUrl}
                      alt={draft.directDeposit.document.name}
                      className={classes.filePreviewImg}
                    />
                  ) : (
                    <UploadDropIcon />
                  )}
                  <p className={classes.dropzonePrimary}>Document uploaded</p>
                </div>
                <div className={classes.fileActions}>
                  <button
                    type="button"
                    className={classes.linkButton}
                    onClick={() => setPreviewOpen(true)}
                    disabled={!draft.directDeposit.document.previewUrl}
                  >
                    Preview
                  </button>
                  <button type="button" className={classes.linkButton} onClick={setup.removeDocument}>
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <DropzoneArea
                acceptedFiles={RB_PAY_ALLOWED_DOCUMENT_TYPES}
                filesLimit={1}
                maxFileSize={RB_PAY_MAX_DOCUMENT_SIZE}
                showAlerts={false}
                showPreviewsInDropzone={false}
                dropzoneClass={classes.dropzoneEmpty}
                dropzoneText={isUploading ? "Uploading…" : "No document uploaded"}
                onChange={handleDropFiles}
                onDropRejected={handleDropRejected}
              />
            )}
            <p className={classes.uploadHelp}>{RB_PAY_BANK_DOCUMENT_HELPER_TEXT}</p>
            {renderFieldError(documentUploadError || setup.getFieldError("directDeposit.document"))}
          </div>
        ) : (
          <div className={classes.sectionDivider}>
            <div className={classes.statusHeadingRow}>
              <p className={classes.subHeading} style={{ margin: 0 }}>
                Please provide your Email Address setup for auto deposit to receive payments
              </p>
              <span
                className={`${classes.statusChip} ${
                  isEmailVerified ? classes.chipVerified : classes.chipUnverified
                }`}
              >
                {isEmailVerified ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" fill={C.successIcon} />
                      <path d="M5 8l2.5 2.5 4-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Verified
                  </>
                ) : (
                  "Not verified"
                )}
              </span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className={classes.inputLabel}>Email Address</label>
              <input
                type="email"
                className={classes.input}
                style={{ maxWidth: 400 }}
                value={draft.emailTransfer.email}
                placeholder="your@email.com"
                onChange={(event) => setup.updateDraft("emailTransfer.email", event.target.value)}
                onBlur={() => setup.markFieldTouched("emailTransfer.email")}
              />
              {renderFieldError(setup.getFieldError("emailTransfer.email"))}
            </div>

            {isEmailVerified ? (
              <>
                <p className={classes.verifiedText}>Verified: {draft.emailTransfer.email}</p>
                <p className={classes.reverifyHint}>Editing this email will require re-verification.</p>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    className={classes.sendCodeButton}
                    onClick={setup.requestVerificationCode}
                    disabled={
                      setup.verificationState.isRequesting ||
                      setup.verificationState.isVerifying ||
                      !emailLooksValid
                    }
                  >
                    Verify Email
                  </button>
                </div>
                <div className={classes.emailNotice}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="8" cy="8" r="6.5" stroke={C.warnIcon} strokeWidth="1.4" />
                    <line x1="8" y1="4.5" x2="8" y2="9" stroke={C.warnIcon} strokeWidth="1.4" strokeLinecap="round" />
                    <circle cx="8" cy="11.2" r="0.8" fill={C.warnIcon} />
                  </svg>
                  <span>You&apos;ll need to verify this email before you can finish setup.</span>
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  };

  // ── Authorization section ─────────────────────────────────────────
  const renderAuthorizationBody = () => {
    if (!isFormMode) {
      return (
        <div className={classes.successBox}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="8" cy="8" r="7" fill={C.successIcon} />
            <path d="M5 8l2.5 2.5 4-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className={classes.successText}>
            {isDirectDeposit
              ? renderRBPayText("You have authorized Relief Buddy Inc. via RB Pay to deposit payments into your bank account.")
              : "You have certified that your email is set up for Auto Deposit to receive payments."}
          </p>
        </div>
      );
    }

    const isAuthorized = isDirectDeposit
      ? draft.directDeposit.authorizationAccepted
      : draft.emailTransfer.certificationAccepted;
    const authPath = isDirectDeposit ? "directDeposit.authorizationAccepted" : "emailTransfer.certificationAccepted";
    const authText = isDirectDeposit ? RB_PAY_DIRECT_DEPOSIT_AUTHORIZATION_TEXT : RB_PAY_EMAIL_CERTIFICATION_TEXT;

    return (
      <>
        <div className={classes.checkboxBox} style={{ padding: "16px 18px" }}>
          <input
            type="checkbox"
            id="main-auth"
            className={classes.checkbox}
            style={{ width: 16, height: 16, marginTop: 3 }}
            checked={isAuthorized}
            onChange={(event) => setup.updateDraft(authPath, event.target.checked)}
          />
          <label htmlFor="main-auth" className={classes.checkboxLabel} style={{ lineHeight: 1.75 }}>
            {authText}
          </label>
        </div>
        {renderFieldError(setup.getFieldError(authPath))}
      </>
    );
  };

  return (
    <>
      <RBAppbar barName="RB Pay Settings" barChildren={appbarActions} />
      <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.stickyChecklist} ref={checklistRef}>
          <RBPayStepProgress
            steps={steps}
            activeStepKey={activeStepKey}
            isSetupComplete={isSetupComplete}
            notice={setupNotice}
            theme={brandTheme}
            onStepClick={scrollToSection}
          />
        </div>

        <div className={classes.banner}>
          <div className={classes.bannerHead}>
            <div className={classes.bannerIcon}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="4.5" r="1" fill="white" />
                <rect x="6" y="7" width="2" height="4.5" rx="1" fill="white" />
                <circle cx="7" cy="7" r="6" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
              </svg>
            </div>
            <h2 className={classes.bannerTitle}>{renderRBPayText("About RB Pay")}</h2>
          </div>
          <p className={classes.bannerBody}>{industryConfig.description}</p>
          <div className={classes.bannerFooter}>
            <p className={classes.bannerMeta}>
              Questions? Contact us{" "}
              <a className={classes.bannerLink} href={contactMailto}>
                {contactEmailAddress}
              </a>
            </p>
          </div>
        </div>

        <div ref={profileSectionRef}>
          <RBPaySetupSection
            stepNumber="01"
            title="Profile Information"
            description={
              supportsProfileTypeSelection
                ? "Choose your payroll profile type and enter your tax or business details."
                : "Manage your payroll profile details independently from your payment method settings."
            }
            theme={brandTheme}
          >
            {renderProfileBody()}
          </RBPaySetupSection>
        </div>

        <div ref={paymentSectionRef}>
          <RBPaySetupSection
            stepNumber="02"
            title="Payment Method"
            description={renderRBPayText("Choose how you receive RB Pay payments.")}
            theme={brandTheme}
          >
            {renderPaymentBody()}
          </RBPaySetupSection>
        </div>

        <div ref={authSectionRef}>
          <RBPaySetupSection
            stepNumber="03"
            title="Authorization"
            description={renderRBPayText("Review and authorize your RB Pay payment setup.")}
            theme={brandTheme}
          >
            {renderAuthorizationBody()}
          </RBPaySetupSection>
        </div>

        {errorMessage ? <div className={classes.sectionError}>{errorMessage}</div> : null}

        {isFormMode ? (
          <div className={classes.actionRow}>
            <button type="button" className={classes.cancelButton} onClick={handleCancel} disabled={isSaving}>
              Cancel
            </button>
            <Tooltip title={isEditing && !isDirty ? RB_PAY_NO_CHANGES_TOOLTIP : ""} arrow>
              <span style={{ display: "inline-flex" }}>
                <button
                  type="button"
                  className={classes.saveButton}
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  {isSaving ? "Saving…" : saveLabel}
                </button>
              </span>
            </Tooltip>
          </div>
        ) : (
          <div className={classes.actionRow}>
            <button type="button" className={classes.saveButton} onClick={setup.startEdit}>
              Edit
            </button>
          </div>
        )}
      </div>

      <RBPayDocumentPreviewModal
        open={previewOpen}
        previewUrl={draft.directDeposit.document?.previewUrl || ""}
        fileType={draft.directDeposit.document?.type || ""}
        fileName={draft.directDeposit.document?.name || "Uploaded Document"}
        onClose={() => setPreviewOpen(false)}
      />

      <RBPayEmailVerificationModal
        open={setup.verificationState.open}
        email={setup.verificationState.email}
        code={setup.verificationState.code}
        errorMessage={setup.verificationState.errorMessage}
        successMessage={setup.verificationState.successMessage}
        isRequesting={setup.verificationState.isRequesting}
        isVerifying={setup.verificationState.isVerifying}
        resendSecondsLeft={setup.resendSecondsLeft}
        onClose={setup.closeVerificationModal}
        onCodeChange={setup.setVerificationCode}
        onSubmit={setup.submitVerificationCode}
        onResend={setup.requestVerificationCode}
      />

      <RBAlert
        show={successModal !== null}
        alertTitle={successModal === "edit" ? RB_PAY_EDIT_SUBMIT_TITLE : RB_PAY_FIRST_SUBMIT_TITLE}
        alertMessage={successModal === "edit" ? RB_PAY_EDIT_SUBMIT_MESSAGE : RB_PAY_FIRST_SUBMIT_MESSAGE}
        buttons={[
          <RBButton
            key="ok"
            text="OK"
            buttonBackgroundColor={brandTheme.primary}
            onClick={closeSuccessModalAndRedirect}
          />,
        ]}
      />

      <RBAlert
        show={errorModalOpen}
        alertTitle={RB_PAY_SUBMIT_ERROR_TITLE}
        alertMessage={errorMessage || RB_PAY_SUBMIT_ERROR_MESSAGE}
        buttons={[
          <RBButton
            key="ok"
            text="OK"
            buttonBackgroundColor={brandTheme.primary}
            onClick={() => setErrorModalOpen(false)}
          />,
        ]}
      />
      </div>
    </>
  );
};
